import { Injectable, inject } from '@angular/core';
import { Guid } from '@modules/shared/helpers/guid-helper';
import { NotificationService } from '@modules/shared/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject, firstValueFrom, map, of, shareReplay, Subject, Subscription, switchMap, take, takeUntil, withLatestFrom
} from 'rxjs';
import { formatDate, registerLocaleData } from '@angular/common';
import { TaService } from '@modules/core/services/ta.service';
import localeSE from '@angular/common/locales/sv';
import { Router } from '@angular/router';
import { AuthService } from '@modules/core/services';
import { TrafficDeviationTypesUnion } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-union';
import { 
  AbstractControl, FormArray, FormControl, FormGroup, NonNullableFormBuilder, UntypedFormArray, ValidationErrors, Validators 
} from '@angular/forms';
import { Call, RouteInfo, TrafficDeviationForm } from '../interfaces/traffic-deviation-form';
import { TypedForm } from '@modules/shared/interfaces/typed-form/typed-form';
import { AppStateService } from '@modules/core/services/app-state.service';
import { ArrayMinLengthValidator } from '../validators/array-min-length-validator/array-min-length.validator';
import { TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import { FormSelectOption } from '../components/form-select/interfaces/form-select-option';
import { ArrayMaxLengthValidator } from '../validators/array-max-length-validator/array-max-length.validator';
import { LoggingService } from '@modules/shared/services/logging.service';
import { dateRangeValidator } from '../validators/date-range-validator/date-range-validator';
import { checkIfEndDateAfterStartDate, checkPublicationStartNotAfterTimeSpanStart } from '@components/history/customValidators';
import { endDateAfterStartDateValidator, endTimeAfterStartTimeValidator } from '../validators/end-after-start-field-validator/end-after-start-field.validator';
import { EffectedRoutesOnStop } from '../components/searches/components/search-stop/interfaces';

registerLocaleData(localeSE, 'sv');

@Injectable({
  providedIn: 'root'
})
export class TrafficDeviationService {
  private readonly _notificationService = inject(NotificationService);
  private readonly _translateService = inject(TranslateService);
  private readonly _taService = inject(TaService);
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _formBuilder = inject(NonNullableFormBuilder);
  private readonly _appStateService = inject(AppStateService);
  private readonly _trafficDeviationsStore = inject(TrafficDeviationsStore);
  private readonly _logger = inject(LoggingService);

  public trafficDeviationForm: FormGroup<TypedForm<TrafficDeviationForm>>;
  public readonly swedishTranslations$ = this._translateService.currentLoader
    .getTranslation('sv_se')
    .pipe(shareReplay(1));

  public readonly deviationScope$ = this._trafficDeviationsStore.deviationScope$;
  public readonly selectedDeviationType$ = this._trafficDeviationsStore.selectedDeviationType$;
  public readonly automaticVariables$ = this._trafficDeviationsStore.automaticVariables$;
  public readonly automaticRouteInfoVariable$ = this._trafficDeviationsStore.automaticRouteInfoVariable$;
  public readonly deviationType$ = this.selectedDeviationType$.pipe(
    map((type) => ({
      deviationInformation: type === 'DeviationInformation',
      journeyPattern: type === 'ChangeOfJourneyPattern',
      journeyTiming: type === 'ChangeOfJourneyTiming',
      predictionInaccurate: type === 'PredictionInaccurate',
      connectionCancellation: type === 'ConnectionCancellation',
    }))
  );
  public readonly trafficDeviationsConfig$ = this._appStateService.getPropertyState('trafficDeviationsConfig');
  public readonly toggleTrafficDeviationInformation$ = new BehaviorSubject<boolean>(true);
  public readonly existingTrafficDeviationLoaded$ = new BehaviorSubject<boolean>(false);
  public readonly toggleInternalComment$ = new BehaviorSubject<boolean>(false);

  public readonly deviationTypeOptions = new FormControl('');
  public readonly deviationTypeOptions$: BehaviorSubject<ReadonlyArray<FormSelectOption>> = new BehaviorSubject([]);

  constructor() { }

  private _deviationScopeSubscription: Subscription;

  public createForm(): void {
    const formDates = this.initializeFormDates();

    this.trafficDeviationForm = this._formBuilder.group({
      version: [0],
      userFullName: [''],
      deviationReason: ['', [Validators.maxLength(50)]],
      hastusRegion: ['', [Validators.maxLength(50)]],
      garageCode: ['', [Validators.maxLength(50)]],
      internalComment: ['', [Validators.maxLength(255)]],
      affected: this._formBuilder.group({
        routeInfo: this._formBuilder.array([this._formBuilder.group({
          routeId: ['', [Validators.maxLength(50)]],
          routeName: ['', [Validators.maxLength(50)]],
          routePublicName: ['', [Validators.maxLength(50)]],
          direction: [undefined],
          firstStopDestinationSign: ['', [Validators.maxLength(50)]],
          tripNumber: ['', [Validators.maxLength(50), Validators.pattern('^[0-9]+$')]],
          vehicleTaskNumber: ['', [Validators.maxLength(50)]],
          trafficDay: ['', [Validators.maxLength(50)]],
          calls: this._formBuilder.array([this._formBuilder.group({
            placeId: ['', [Validators.maxLength(50)]],
            parentId: ['', [Validators.maxLength(50)]],
            name: ['', [Validators.maxLength(50)]],
            platformCode: ['', [Validators.maxLength(50)]],
            placeLatitude: [undefined],
            placeLongitude: [undefined],
            replacePlaceId: ['', [Validators.maxLength(50)]],
            replaceName: ['', [Validators.maxLength(50)]],
            effectedRoutes: this._formBuilder.array([this._formBuilder.group({
              firstStopDestinationSign: ['', [Validators.maxLength(50)]],
              routeName: ['', [Validators.maxLength(50)]]
            })])
          })]),
          transportAuthorityNumber: ['', [Validators.maxLength(50)]],
          stopCentric: false
        })], [ArrayMinLengthValidator(1)]),
        startDate: [formDates.startTime.toLocaleDateString('sv'), [
          Validators.maxLength(50), 
          dateRangeValidator({ 
            minDate: formDates.firstAllowedDate, 
            maxDate: formDates.maxDate 
          })
        ]],
        startTime: [formDates.startTime.toLocaleTimeString('sv'), [Validators.maxLength(50)]],
        endDate: [formDates.endTime.toLocaleDateString('sv'), [
          Validators.maxLength(50), dateRangeValidator({ 
            minDate: formDates.firstAllowedDate, 
            maxDate: formDates.maxDate 
          }),
          endDateAfterStartDateValidator
        ]],
        endTime: [formDates.endTime.toLocaleTimeString('sv'), [Validators.maxLength(50), endTimeAfterStartTimeValidator]],
        minutes: ['', [
          Validators.maxLength(50), 
          Validators.pattern("^[0-9][0-9]*$")
        ]],
        affectedTrip: this._formBuilder.group({
          startPlace: [''],
          endPlace: [''],
        })
      }, { validators: checkIfEndDateAfterStartDate }),
      trafficDeviationInformation: this._formBuilder.group({
        templateId: ['', [Validators.maxLength(50)]],
        version: [0],
        summary: ['', [Validators.maxLength(3000)]],
        header: ['', [Validators.maxLength(3000)]],
        content: ['', [Validators.maxLength(3000)]],
        displayScope: this._formBuilder.group({
          signDisplay: [true],
          journeyPlanner: [true],
          advancedOptions: ['', [Validators.maxLength(50)]]
        }),
        publicationStartDate: ['', [
          Validators.maxLength(50), dateRangeValidator({ 
            minDate: formDates.firstAllowedDate, 
            maxDate: formDates.maxDate 
          })
        ]],
        publicationStartTime: ['', [Validators.maxLength(50)]],
        publicationEndDate: ['', [
          Validators.maxLength(50), dateRangeValidator({ 
            minDate: formDates.firstAllowedDate, 
            maxDate: formDates.maxDate 
          })
        ]],
        publicationEndTime: ['', [Validators.maxLength(50)]],
        stopScope: ['LineCentric', [Validators.maxLength(50)]],
        allChipsHasValue: [true, [
          (control: AbstractControl): ValidationErrors | null => {
            return control.value === true ? null : { allChipsHasValue: true };
          }
        ]], 
        custom: new UntypedFormArray([this._formBuilder.group({ 
          'Time': ['', [Validators.maxLength(50)]],
          'Departures': ['', [
            Validators.maxLength(50), 
            Validators.pattern('^[0-9]{2}:[0-9]{2}(((, | och )[0-9]{2}:[0-9]{2})?)+$')
          ]],
          'Date and Time': ['', [Validators.maxLength(50), dateRangeValidator({ 
            minDate: formDates.minDate, 
            maxDate: formDates.maxDate 
          })]],
          'CustomFieldDescription': ['', [Validators.maxLength(255)]],
          'Amount of Minutes': ['', [
            Validators.maxLength(50), 
            Validators.max(120), 
            Validators.pattern('^[1-9][0-9]*$|^0$')
          ]]
        })])
      }),
    }, { validators: checkPublicationStartNotAfterTimeSpanStart });
  }

  public initializeDeviationTypeOptions(destroy: Subject<void>) {
    if (this._deviationScopeSubscription) {
      this._deviationScopeSubscription.unsubscribe();
    }

    this._deviationScopeSubscription = this.deviationScope$.pipe(
      withLatestFrom(this.deviationType$),
      switchMap(([scope, type]) => {
        switch (scope) {
          case 'TRIP':
            return of([
              { text: 'ChangeOfJourneyTiming', value: 'ChangeOfJourneyTiming', selected: type.journeyTiming },
              { text: 'PredictionInaccurate', value: 'PredictionInaccurate', selected: type.predictionInaccurate },
              { text: 'DeviationInformation', value: 'DeviationInformation', selected: type.deviationInformation },
              { text: 'ConnectionCancellation', value: 'ConnectionCancellation', selected: type.connectionCancellation },
            ])
          case 'COMING_TRIP':
              return of([
                { text: 'PredictionInaccurate', value: 'PredictionInaccurate', selected: type.predictionInaccurate },
                { text: 'DeviationInformation', value: 'DeviationInformation', selected: type.deviationInformation },
              ])
          default:
            return of([
              { text: 'DeviationInformation', value: 'DeviationInformation', selected: type.deviationInformation },
              { text: 'ChangeOfJourneyPattern', value: 'ChangeOfJourneyPattern', selected: type.journeyPattern },
            ])
        }
      }),  
      withLatestFrom(this.selectedDeviationType$),
      takeUntil(destroy)
    ).subscribe(([options, value]) => {
      this.deviationTypeOptions.setValue(value ?? `${options[0].value}`);
      this.deviationTypeOptions$.next(options);
    });
  }

  public async postTrafficDeviation(partialRequestObject: TrafficDeviationTypesUnion)
  : Promise<void> {
    const requestObject = {
      ...partialRequestObject,
      id: this.generateId(partialRequestObject),
      username: this._authService.user.userid
    };

    try {
      await firstValueFrom(this._taService.postTrafficDeviation(requestObject));
      
      this._notificationService.openText(
        this._translateService.instant('Traffic Deviation post successful!')
      );
    } catch (error) {
      this._notificationService.openError(
        this._translateService.instant('Traffic Deviation post unsuccessful!')
      );
      this._logger.logException(error);
    }
  }

  public async putTrafficDeviation(requestObject: TrafficDeviationTypesUnion): Promise<void> {
    try {
      requestObject.username = this._authService.user.userid;
      await firstValueFrom(this._taService.putTrafficDeviation(requestObject));

      this._trafficDeviationsStore.deviationPutSuccess$.next();
      this._router.navigate(['', { outlets: { outlet0: null }}]);

      this._notificationService.openText(
        this._translateService.instant('Traffic Deviation Put successful!')
      );
    } catch (error) {
      this._notificationService.openError(
        this._translateService.instant('Traffic Deviation Put unsuccessful!')
      );
      this._logger.logException(error);
    }
  }  

  public async recallTrafficDeviation(id: string): Promise<void> {
    try {
      await firstValueFrom(this._taService.recallTrafficDeviation(id));
      
      this._notificationService.openText(
        this._translateService.instant('Recall Traffic Deviation post successful')
      );
    } catch (error) {
      this._notificationService.openError(
        this._translateService.instant('Recall Traffic Deviation post unsuccessful!')
      );
      this._logger.logException(error);
    }
  }

  public formatAndOrCombineDateAndTime(dateString?: string, timeString?: string)
  : string | undefined {
    if (!dateString) return undefined;

    let date = new Date(dateString);
    if (timeString) {
      const splitTime = timeString.split(':');
      date.setHours(+splitTime[0]);
      date.setMinutes(+splitTime[1]);

      return formatDate(date, 'yyyy-MM-ddTHH:mm:ssZZZZZ', 'sv-SE');
    }
    
    return formatDate(date, 'yyyy-MM-dd', 'sv-SE');
  }

  public splitTimeFromDate(dateString?: string): string | undefined {
    const dateHasTime = dateString?.indexOf('T') !== -1;
    return dateHasTime ? new Date(dateString).toLocaleTimeString('sv-SE') : undefined;
  }

  public formatDateString(dateString?: string): string | undefined {
    return dateString ? new Date(dateString).toLocaleDateString('sv-SE') : undefined;
  }

  public addCall({ 
    placeId = '', 
    parentId = '',
    name = '',
    platformCode = '',
    placeLatitude = undefined,
    placeLongitude = undefined,
    replacePlaceId = '', 
    replaceName = '',
    effectedRoutes = [] as EffectedRoutesOnStop[]
  }: {
    placeId?: string, 
    parentId?: string,
    name?: string,
    platformCode?: string,
    placeLatitude?: number,
    placeLongitude?: number,
    replacePlaceId?: string, 
    replaceName?: string,
    effectedRoutes?: EffectedRoutesOnStop[]
  } = {}
  ): FormGroup<TypedForm<Call>> {
    const call = this._formBuilder.group({ 
      placeId: [placeId, [Validators.maxLength(50)]],
      parentId: [parentId, [Validators.maxLength(50)]],
      name: [name, [Validators.maxLength(50)]],
      platformCode: [platformCode, [Validators.maxLength(50)]],
      placeLatitude: [placeLatitude],
      placeLongitude: [placeLongitude],
      replacePlaceId: [replacePlaceId, Validators.maxLength(50)],
      replaceName: [replaceName, [Validators.maxLength(50)]],
      effectedRoutes: this._formBuilder.array(
        effectedRoutes.filter((r) => r.active).map((route) => {
          return this._formBuilder.group({
            firstStopDestinationSign: [route.firstStopDestinationSign, [Validators.maxLength(50)]],
            routeName: [route.routeName, [Validators.maxLength(50)]]
          });
      }))
    });
    return call;
  }

  public addRouteInfo(
    routeInfo: {
      routeId?: string; routeName?: string; routePublicName?: string; direction?: number, 
      firstStopDestinationSign?: string; tripNumber?: string;  trafficDay?: string; 
      vehicleTaskNumber?: string; stopCentric?: boolean;
    } = { routeId: '', routeName: '',  routePublicName: '', direction: undefined, 
      firstStopDestinationSign: '',  tripNumber: '',  trafficDay: '', vehicleTaskNumber: '', 
      stopCentric: false
    },
    calls?: FormGroup<TypedForm<Call>>[],
  ): FormGroup<TypedForm<RouteInfo>> {
    const formRouteInfo = this._formBuilder.group({
      routeId: [routeInfo.routeId, [Validators.maxLength(50)]],
      routeName: [routeInfo.routeName, [Validators.maxLength(50)]],
      routePublicName: [routeInfo.routePublicName, [Validators.maxLength(50)]],
      tripNumber: [routeInfo.tripNumber, [Validators.maxLength(50), Validators.pattern('^[0-9]+$')]],
      vehicleTaskNumber: [routeInfo.vehicleTaskNumber, Validators.maxLength(50)],
      direction: [routeInfo.direction],
      firstStopDestinationSign: [routeInfo.firstStopDestinationSign, Validators.maxLength(50)],
      trafficDay: [this.formatDateString(routeInfo.trafficDay), Validators.maxLength(50)],
      calls: this._formBuilder.array(calls ?? []),
      transportAuthorityNumber: ['', Validators.maxLength(50)],
      stopCentric: routeInfo.stopCentric
    });
    return formRouteInfo;
  }

  public resetStates(): void {
    this.toggleTrafficDeviationInformation$.next(true);
    this.toggleInternalComment$.next(false);
    this._trafficDeviationsStore.updateTrafficDeviationState({deviationScope: 'DEFAULT', selectedDeviationType: undefined});
    this.existingTrafficDeviationLoaded$.next(false);
  }

  /**
   * Adds validators for all controls
   * @param form: FormGroup<TypedForm<TrafficDeviationForm>>
   * @param selectedTrafficDeviationType: TrafficDeviationTypesUnion['type']
   */
  public addRequiredValidators(): void {
    const affected = this.trafficDeviationForm.controls.affected.controls;
    const routeInfo = affected.routeInfo;
    
    this._addValidators(this.trafficDeviationForm.controls.deviationReason);
    this._addValidators(this.trafficDeviationForm.controls.hastusRegion);
    this._addValidators(this.trafficDeviationForm.controls.garageCode);
    this._addValidators(affected.startDate);
    this._addValidators(affected.startTime);
    this._addValidators(affected.endDate);
    this._addValidators(affected.endTime);
    
    routeInfo.controls.forEach(({controls}) => {
      this._addValidators(controls.routeName);
    });

    if (
      this.toggleTrafficDeviationInformation$.value 
      && this._trafficDeviationsStore.stateSnapshot.selectedDeviationType !== 'ConnectionCancellation') {
      this.addDeviationInformationValidation();
    }

    if (this.toggleInternalComment$.value) {
      this._addValidators(this.trafficDeviationForm.controls.internalComment);
    }

    switch (this._trafficDeviationsStore.stateSnapshot.selectedDeviationType) {
      case 'ChangeOfJourneyPattern':
        routeInfo.controls.forEach(({controls}) => {
          this._addValidators(controls.routeName);
          controls.calls.controls.forEach(({controls}) => {
            this._addValidators(controls.placeId);
            this._addValidators(controls.name);
            this._addValidators(controls.replaceName);
            this._addValidators(controls.replacePlaceId);
          });
        });
        break;
      case 'ChangeOfJourneyTiming':
        routeInfo.controls.forEach(({controls}) => {
          this._addValidators(controls.routeName);
          this._addValidators(controls.vehicleTaskNumber);
          this._addValidators(controls.tripNumber);
          this._addValidators(controls.trafficDay);
          this._addValidators(controls.calls, [ArrayMinLengthValidator(2)]);
          controls.calls.controls.forEach(({controls}) => {
            this._addValidators(controls.placeId);
            this._addValidators(controls.name);
          });
        });
        this._addValidators(affected.minutes);
        break;
      case 'PredictionInaccurate':
        routeInfo.controls.forEach(({controls}) => {
          this._addValidators(controls.routeName);
          this._addValidators(controls.vehicleTaskNumber);
          this._addValidators(controls.tripNumber);
        });
        this._addValidators(affected.startTime);
        break;
      case 'ConnectionCancellation':
        routeInfo.controls.forEach(({controls}) => {
          this._addValidators(controls.routeName);
          this._addValidators(controls.tripNumber);
          this._addValidators(controls.trafficDay);
          this._addValidators(controls.transportAuthorityNumber);
          this._addValidators(controls.calls, [ArrayMaxLengthValidator(1)])
        });
        break;
      case 'DeviationInformation':
        if (!this.toggleTrafficDeviationInformation$.value) {
          this.addDeviationInformationValidation();
        }
        break;
    }
  }

  /**
   * Adds required validators for controls regarding the traffic deviation information block.
   */
  public addDeviationInformationValidation(): void {
    const deviationInformation = this.trafficDeviationForm.controls.trafficDeviationInformation;
    this._addValidators(deviationInformation.controls.content);
    this._addValidators(deviationInformation.controls.header);
    this._addValidators(deviationInformation.controls.summary);
  }

  /**
   * Removes required validators for controls regarding the traffic deviation information block.
   */
  public removeDeviationInformationValidation(): void {
    const deviationInformation = this.trafficDeviationForm.controls.trafficDeviationInformation;
    this.removeValidators(deviationInformation.controls.content);
    this.removeValidators(deviationInformation.controls.header);
    this.removeValidators(deviationInformation.controls.summary);
  }

  /**
   * Remove validator for specific control.
   * If no validators passed as parameter, required is set as default
   * @param control: AbstractControl
   * @param validators?: Array<((control: AbstractControl) => ValidationErrors | null)>
   */
  public removeValidators(
    control: AbstractControl, 
    validators?: Array<((control: AbstractControl) => ValidationErrors | null)>
  ): void {
    if (!validators) control.removeValidators([Validators.required]);
    if (validators) control.removeValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Adds validator for specific control.
   * If no validators passed as parameter, required is set as default
   * @param control: AbstractControl
   * @param validators?: Array<((control: AbstractControl) => ValidationErrors | null)>
   */
  private _addValidators(
    control: AbstractControl, 
    validators?: Array<((control: AbstractControl) => ValidationErrors | null)>
  ): void {
    if (!validators) control.addValidators([Validators.required]);
    if (validators) control.addValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }
  
  /**
   * Resets the required validators for all controls.
   */
  public resetRequiredValidators(): void { 
    this._removeRequiredValidators(this.trafficDeviationForm);
    this.addRequiredValidators();
  }

  private initializeFormDates(): { 
    startTime: Date; endTime: Date; minDate: string; maxDate: string; firstAllowedDate: string; 
  } {
    const startTime = new Date();
    const endTime = new Date();
    startTime.setSeconds(0);
    endTime.setMinutes(endTime.getMinutes() + 30);
    endTime.setSeconds(0);

    let date = new Date();
    let minDate: string;
    date.setHours(0, 0, 0, 0);
    minDate = date.toLocaleString('sv').replace(' ', 'T');

    date = new Date();
    let maxDate: string;
    date.setFullYear(date.getFullYear() + 1);
    date.setHours(23, 59, 59, 0);
    maxDate = date.toLocaleString('sv').replace(' ', 'T');

    date = new Date();
    let firstAllowedDate: string;
    date.setFullYear(date.getFullYear() - 1);
    date.setHours(0, 0, 0, 0);
    firstAllowedDate = date.toLocaleString('sv').replace(' ', 'T');

    return { startTime, endTime, minDate, maxDate, firstAllowedDate };
  }

  private generateId(partialRequestObject: TrafficDeviationTypesUnion): string {
    if (partialRequestObject.type === 'ChangeOfJourneyTiming') {
      const journeyTiming = partialRequestObject;
      const scope = journeyTiming.trafficDeviationScope;
      const routeInfo = scope.routeInfo?.[0];
      return `OMS.DT-${routeInfo.trafficDay}:${journeyTiming.hastusRegion}:${routeInfo.routeName}:${routeInfo.tripNumber}`;
    }

    return `OMS.MAP-${Guid.newGuid()}`;
  }

  /**
   * Iterates through all controls and removes all required validators.
   * @param control: AbstractControl 
   */
  private _removeRequiredValidators(control: AbstractControl): void {
    this.removeValidators(control);
    if (control instanceof FormGroup || control instanceof FormArray) {
      for (const key in control.controls) {
        this._removeRequiredValidators(control.get(key));
      }
    }
  }
}